import { Stack, Typography } from '@mui/material';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSearchSelector from 'features/ui/base-components/pai-search-selector';
import PaiSelect from 'features/ui/base-components/pai-select';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import BlacklistItemChip from './blacklist-item-chip';
import { BlacklistTypes } from './company-blacklist.types';

interface Props {
  linkedinAccounts: LinkedinAccount[];
  selectedLinkedinAccount: string;
  companyQuery: string;
  companyOptions: string[][];
  selectedCompanies: string[][];
  companyLoading: boolean;
  companyIsEmpty: boolean;

  handleLinkedinAccountChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleCompanyQueryChange(event: React.ChangeEvent<HTMLInputElement>): void;
  searchCompanyKeyUp(): void;
  searchCompanyKeyDown(): void;
  handleToggleCompany(value: string): () => void;
  handleRemoveCompany(value: string): () => void;
}

const SelectCompanyItems: React.FC<Props> = ({
  linkedinAccounts,
  selectedLinkedinAccount,
  companyQuery,
  companyOptions,
  selectedCompanies,
  companyLoading,
  companyIsEmpty,
  handleLinkedinAccountChange,
  handleCompanyQueryChange,
  searchCompanyKeyUp,
  searchCompanyKeyDown,
  handleToggleCompany,
  handleRemoveCompany,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const accountOptions = useMemo(
    () =>
      linkedinAccounts.map((account) => ({
        label: account.userEmail,
        value: account._id,
      })),
    [linkedinAccounts]
  );

  const pickerOptions = useMemo(
    () =>
      companyOptions.map((option) => ({
        label: option[1],
        value: option[0],
      })),
    [companyOptions]
  );

  const selectedValues = useMemo(() => selectedCompanies.map((item) => item[0]), [selectedCompanies]);

  return (
    <Stack gap={2}>
      <PaiSelect value={selectedLinkedinAccount} label={t('selectLinkedinAccount')} onChange={handleLinkedinAccountChange}>
        {accountOptions.map((option) => (
          <PaiMenuItem key={option.value} value={option.value}>
            {option.label}
          </PaiMenuItem>
        ))}
      </PaiSelect>

      <PaiSearchSelector
        placeholder={t('typeInOrPasteCompanyNames')}
        value={companyQuery}
        selectedValues={selectedValues}
        options={pickerOptions}
        onChange={handleCompanyQueryChange}
        onKeyUp={searchCompanyKeyUp}
        onKeyDown={searchCompanyKeyDown}
        onMenuItemClick={handleToggleCompany}
        multiselect={true}
        loading={companyLoading}
        disabled={!selectedLinkedinAccount || companyLoading}
      />

      {companyIsEmpty ? <Typography className={classes.errorText}>{t('noResultsFound')}</Typography> : null}

      {!_.isEmpty(selectedCompanies) ? (
        <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
          {selectedCompanies.map((item) => (
            <BlacklistItemChip
              key={item[0]}
              label={item[1]}
              blacklistType={BlacklistTypes.COMPANY}
              handleRemove={handleRemoveCompany(item[0])}
            />
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  errorText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.error.main,
    padding: '0 8px',
  },
}));

export default SelectCompanyItems;
